import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridPageChangeEvent,
  GridRowClickEvent,
} from "@progress/kendo-react-grid";
import { orderBy, SortDescriptor } from "@progress/kendo-data-query";
import { useDeals } from "src/dm/hooks/useDeals";
import { useDashboardPreferences } from "src/dm/hooks/useDashboardPreferences";
import UserName from "src/dm/components/UserName/UserName";
import { useUserNamesBatch } from "src/dm/hooks/useUserNames";
import { DealListItem } from "src/dm/interfaces/Deal";
import { PAGE_SIZE_DEFAULT } from "src/dm/utils/constants";

// Column metadata mirrors the customization editor's catalog. Kept local so
// the dashboard can render Kendo GridColumn elements with the right width
// without round-tripping to the catalog endpoint.
const COLUMN_META: Record<string, { title: string; width?: number }> = {
  dealId: { title: "Deal ID", width: 100 },
  description: { title: "Description" },
  bannerCode: { title: "Banner", width: 100 },
  status: { title: "Status", width: 140 },
  statusDate: { title: "Status Date", width: 120 },
  adStartDate: { title: "Ad Start", width: 120 },
  adEndDate: { title: "Ad End", width: 120 },
  promoStartDate: { title: "Promo Start", width: 120 },
  promoEndDate: { title: "Promo End", width: 120 },
  ownerUserId: { title: "Owner", width: 180 },
  isLocked: { title: "Locked", width: 90 },
  isActionRequired: { title: "Action Req'd", width: 110 },
  isInternalDeal: { title: "Internal", width: 90 },
};

// Default fallback when the prefs API hasn't responded yet — matches the
// pre-customization shape so first render doesn't flash with a different
// column set.
const FALLBACK_COLUMNS = [
  "dealId",
  "description",
  "bannerCode",
  "status",
  "adStartDate",
  "adEndDate",
  "ownerUserId",
];

const DashboardPage = () => {
  const navigate = useNavigate();
  const { data: prefsResponse } = useDashboardPreferences();
  const prefs = prefsResponse?.customization;

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(prefs?.pageSize ?? PAGE_SIZE_DEFAULT);
  // Once the prefs query resolves, re-seat the page-size to the saved value
  // (only if the user hasn't already changed it via the grid pager). The
  // explicit user-changed flag prevents prefs from clobbering a fresh
  // manual pick while the user is browsing.
  const [pageSizeManuallyChanged, setPageSizeManuallyChanged] = useState(false);
  useEffect(() => {
    if (prefs?.pageSize && !pageSizeManuallyChanged) {
      setPageSize(prefs.pageSize);
    }
  }, [prefs?.pageSize, pageSizeManuallyChanged]);

  // Apply saved filters to the deals query. These are intentionally read-
  // only on the dashboard — the user changes them from the customization
  // editor; advanced search has its own filter UI.
  const dealsParams = useMemo(() => {
    const statuses = prefs?.defaultStatusFilter ?? [];
    return {
      page,
      pageSize,
      statuses: statuses.length > 0 ? statuses : undefined,
      bannerCode: prefs?.defaultBannerCode || undefined,
      // The API List endpoint supports a single categoryManagerCode today.
      // Pick the first saved one for now; multi-CM scoping is a follow-up
      // (the editor stores the list so the data is ready when the API
      // catches up).
      categoryManagerCode:
        prefs?.defaultCategoryManagerCodes?.[0] ?? undefined,
    };
  }, [page, pageSize, prefs]);

  const { data, isLoading, isError, error } = useDeals(dealsParams);

  const handlePageChange = (e: GridPageChangeEvent) => {
    setPage(Math.floor(e.page.skip / e.page.take) + 1);
    setPageSize(e.page.take);
    setPageSizeManuallyChanged(true);
  };

  const handleRowClick = (e: GridRowClickEvent) => {
    const row = e.dataItem as DealListItem;
    if (row?.dealId) navigate(`/dm/deals/${row.dealId}`);
  };

  // Stable reference for `items` so dependent useMemos don't recompute on
  // every render (react-hooks/exhaustive-deps).
  const items = useMemo(() => data?.items ?? [], [data?.items]);
  const total = data?.total ?? 0;

  // Pre-warm the user-name resolver cache for every owner id on the visible
  // page so each per-row <UserName> renders the resolved name on the first
  // paint instead of the raw DOMAIN\login id. audit/26-gap-closure-
  // verification.md §3 item 7 — was previously rendered as a plain
  // GridColumn which produced "GiantEagle\1179482" style values in the UI.
  //
  // IMPORTANT: this useMemo + the useUserNamesBatch hook below must live
  // ABOVE the isLoading/isError early returns. Previously they sat below
  // those guards, which meant the hook count changed between the loading
  // render and the post-load render — React fired "Rendered more hooks
  // than during the previous render" and the error boundary replaced the
  // dashboard with the router's error UI. That broke pagination (the
  // pager never rendered) and was the root cause of the "next page
  // throws an error" report.
  const ownerIds = useMemo(
    () => items.map((d) => d.ownerUserId).filter((u): u is string => !!u),
    [items],
  );
  useUserNamesBatch(ownerIds);

  if (isLoading) return <p>Loading deals…</p>;
  if (isError) {
    const message = error instanceof Error ? error.message : "unknown error";
    return (
      <div>
        <h2>Could not load deals</h2>
        <p style={{ color: "#b91c1c" }}>{message}</p>
        <p>
          The API is probably not running, the Oracle connection isn't
          configured yet, or there are no matching deals. Once the DB
          connection is set up the grid will populate.
        </p>
      </div>
    );
  }

  // Client-side sort to honour the saved sort column / direction (the API
  // doesn't accept an `orderBy` param yet). Page-bounded so it's O(pageSize).
  const sortDescriptor: SortDescriptor[] = prefs?.sortColumn
    ? [
        {
          field: prefs.sortColumn,
          dir: prefs.sortAscending ? "asc" : "desc",
        },
      ]
    : [];
  const sortedItems = sortDescriptor.length > 0 ? orderBy(items, sortDescriptor) : items;

  const visibleColumns = prefs?.visibleColumns?.length
    ? prefs.visibleColumns
    : FALLBACK_COLUMNS;

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: "1rem",
          marginTop: 0,
        }}
      >
        <h1 style={{ marginTop: 0 }}>Dashboard</h1>
        <Link
          to="/dm/preferences/dashboard"
          style={{ fontSize: "0.85rem", color: "#475569" }}
        >
          Customize…
        </Link>
      </div>
      <p style={{ color: "#555" }}>
        {total} deal{total === 1 ? "" : "s"}
        {prefsResponse?.isCustomized && (
          <span style={{ color: "#94a3b8", marginLeft: "0.5rem", fontSize: "0.85rem" }}>
            (using your saved preferences)
          </span>
        )}
      </p>
      <Grid
        data={sortedItems}
        skip={(page - 1) * pageSize}
        take={pageSize}
        total={total}
        pageable={{ pageSizes: [10, 25, 50, 100] }}
        onPageChange={handlePageChange}
        onRowClick={handleRowClick}
        sortable
        sort={sortDescriptor}
        style={{ cursor: "pointer" }}
      >
        {visibleColumns.map((field) => {
          const meta = COLUMN_META[field];
          if (!meta) return null;
          // Owner column renders through <UserName> so the raw
          // DOMAIN\login id resolves to a person's display name. The
          // useUserNamesBatch call above pre-warms the cache so all 25
          // (or however many) visible rows resolve in a single batch on
          // first render instead of staggered per-row. audit/26 §3.7.
          if (field === "ownerUserId") {
            return (
              <GridColumn
                key={field}
                field={field}
                title={meta.title}
                width={meta.width ? `${meta.width}px` : undefined}
                cells={{
                  data: (props: GridCellProps) => (
                    <td>
                      <UserName userId={(props.dataItem as DealListItem).ownerUserId} />
                    </td>
                  ),
                }}
              />
            );
          }
          return (
            <GridColumn
              key={field}
              field={field}
              title={meta.title}
              width={meta.width ? `${meta.width}px` : undefined}
            />
          );
        })}
      </Grid>
    </div>
  );
};

export default DashboardPage;
